/**
 * Transform JourneyFlowResult (channel → property → outcome) into the
 * 3-column conversion-path shape: channel → subchannel → outcome.
 */

import { CAMPAIGN_CHANNEL_PALETTE, type CampaignChannelKey } from '@/lib/campaigns/fallback-data'
import type { JourneyFlowResult } from '@/lib/measurement/journey-flow'
import {
  CHANNEL_COLOR,
  OUTCOME_COLOR,
  type ConversionFlowData,
  type FlowLink,
  type FlowNode,
} from '@/lib/traffic/conversion-example'

const PURCHASE_OUTCOME = 'outcome:purchase'
const CONVERTED_ID = 'converted'
const BROWSED_ID = 'browsed'

const SUBCHANNEL_TO_CHANNEL: Record<string, CampaignChannelKey> = {
  'Google Ads': 'search',
  'Organic Search': 'search',
  'Meta Ads': 'social',
  'Reddit Ads': 'social',
  'TikTok Ads': 'social',
  'Organic Social': 'social',
  'Roku Ads': 'tv',
  'Universal Ads': 'tv',
  Sonobi: 'programmatic',
  'Other Paid': 'programmatic',
  Direct: 'other',
  Referral: 'other',
}

function subchannelToChannelKey(name: string): CampaignChannelKey {
  return SUBCHANNEL_TO_CHANNEL[name] ?? 'other'
}

function channelNodeId(key: CampaignChannelKey): string {
  return `channel:${key}`
}

function subchannelNodeId(name: string): string {
  return `subchannel:${name}`
}

function pctOfTotal(value: number, total: number): number {
  if (total <= 0) return 0
  return Math.round((value / total) * 100)
}

function pctRate(part: number, whole: number): number {
  if (whole <= 0) return 0
  return Math.round((part / whole) * 100)
}

/**
 * Collapse property and multi-outcome columns into channel → subchannel →
 * converted/browsed. Uses proportional splitting across the property layer
 * so link weights stay conserved.
 */
export function journeyToConversionFlow(flow: JourneyFlowResult): ConversionFlowData {
  const channelToProperty = new Map<string, Map<string, number>>()
  const propertyToOutcome = new Map<string, Map<string, number>>()
  const propertyTotals = new Map<string, number>()

  for (const link of flow.links) {
    if (link.source.startsWith('channel:') && link.target.startsWith('property:')) {
      const sub = link.source.slice('channel:'.length)
      const prop = link.target
      const bucket = channelToProperty.get(sub) ?? new Map<string, number>()
      bucket.set(prop, (bucket.get(prop) ?? 0) + link.value)
      channelToProperty.set(sub, bucket)
      continue
    }
    if (link.source.startsWith('property:') && link.target.startsWith('outcome:')) {
      const prop = link.source
      const bucket = propertyToOutcome.get(prop) ?? new Map<string, number>()
      bucket.set(link.target, (bucket.get(link.target) ?? 0) + link.value)
      propertyToOutcome.set(prop, bucket)
      propertyTotals.set(prop, (propertyTotals.get(prop) ?? 0) + link.value)
    }
  }

  const subchannelTotals = new Map<string, number>()
  const channelTotals = new Map<CampaignChannelKey, number>()
  const subchannelToConverted = new Map<string, number>()
  const subchannelToBrowsed = new Map<string, number>()

  for (const [subchannel, propMap] of channelToProperty) {
    let subTotal = 0
    let converted = 0
    let browsed = 0

    for (const [propId, cpValue] of propMap) {
      subTotal += cpValue
      const propTotal = propertyTotals.get(propId) ?? 0
      if (propTotal <= 0) continue
      const outcomes = propertyToOutcome.get(propId)
      if (!outcomes) continue

      for (const [outcomeId, outcomeValue] of outcomes) {
        const share = cpValue * (outcomeValue / propTotal)
        if (outcomeId === PURCHASE_OUTCOME) converted += share
        else browsed += share
      }
    }

    if (subTotal <= 0) continue
    subchannelTotals.set(subchannel, subTotal)
    subchannelToConverted.set(subchannel, converted)
    subchannelToBrowsed.set(subchannel, browsed)

    const channelKey = subchannelToChannelKey(subchannel)
    channelTotals.set(channelKey, (channelTotals.get(channelKey) ?? 0) + subTotal)
  }

  const grandTotal = flow.totalSessions || Array.from(subchannelTotals.values()).reduce((s, v) => s + v, 0)

  const nodes: FlowNode[] = []
  const links: FlowLink[] = []

  for (const [channelKey, total] of channelTotals) {
    if (total <= 0) continue
    nodes.push({
      id: channelNodeId(channelKey),
      name: CAMPAIGN_CHANNEL_PALETTE[channelKey].label,
      column: 0,
      channel: channelKey,
      kind: 'channel',
    })
  }

  for (const [subchannel, total] of subchannelTotals) {
    if (total <= 0) continue
    const channelKey = subchannelToChannelKey(subchannel)
    nodes.push({
      id: subchannelNodeId(subchannel),
      name: subchannel,
      column: 1,
      channel: channelKey,
      kind: 'subchannel',
      sub: CAMPAIGN_CHANNEL_PALETTE[channelKey].label,
    })

    links.push({
      source: channelNodeId(channelKey),
      target: subchannelNodeId(subchannel),
      value: total,
      pct: pctOfTotal(total, grandTotal),
      pctLabel: 'of all sessions',
    })
  }

  if (subchannelTotals.size > 0) {
    nodes.push(
      {
        id: CONVERTED_ID,
        name: 'Converted',
        column: 2,
        kind: 'outcome',
        color: OUTCOME_COLOR.converted,
        sub: 'Purchased',
      },
      {
        id: BROWSED_ID,
        name: 'Browsed',
        column: 2,
        kind: 'outcome',
        color: OUTCOME_COLOR.browsed,
        sub: 'No purchase',
      },
    )
  }

  for (const [subchannel, subTotal] of subchannelTotals) {
    const converted = Math.round(subchannelToConverted.get(subchannel) ?? 0)
    const browsed = Math.round(subchannelToBrowsed.get(subchannel) ?? 0)

    if (converted > 0) {
      links.push({
        source: subchannelNodeId(subchannel),
        target: CONVERTED_ID,
        value: converted,
        pct: pctRate(converted, subTotal),
        pctLabel: 'conversion rate',
      })
    }
    if (browsed > 0) {
      links.push({
        source: subchannelNodeId(subchannel),
        target: BROWSED_ID,
        value: browsed,
        pct: pctRate(browsed, subTotal),
        pctLabel: 'browsed away',
      })
    }
  }

  return {
    nodes,
    links,
    totals: {
      sessions: flow.totalSessions,
      purchases: flow.totalPurchases,
      revenueCents: flow.totalRevenueCents ?? 0,
    },
  }
}

/** Legend entries for the active nodes in a live flow. */
export function legendForFlow(data: ConversionFlowData): Array<{ label: string; color: string }> {
  const entries: Array<{ label: string; color: string }> = []
  const seen = new Set<string>()

  for (const node of data.nodes) {
    if (node.kind === 'outcome') {
      if (seen.has(node.name)) continue
      seen.add(node.name)
      entries.push({ label: node.name, color: node.color ?? OUTCOME_COLOR.browsed })
      continue
    }
    if (node.kind === 'channel' && node.channel) {
      if (seen.has(node.channel)) continue
      seen.add(node.channel)
      entries.push({
        label: CAMPAIGN_CHANNEL_PALETTE[node.channel].label,
        color: CHANNEL_COLOR[node.channel],
      })
    }
  }

  return entries
}
